
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const GROUP_LOGO = "/logos/tsi-logo.jpg";

const companies = [
  { id:1, name:"TSI",    color:"#0066ff", g2:"#00d4ff", logo:"/logos/tsi-logo.jpg",    live:true  },
  { id:2, name:"Sysarc", color:"#7c3aed", g2:"#bf5fff", logo:"/logos/sysarc-logo.png", live:false },
  { id:3, name:"AIS",    color:"#059669", g2:"#00d4aa", logo:"/logos/ais-logo.png",    live:false },
  { id:4, name:"Tagit",  color:"#ea580c", g2:"#ff6b35", logo:"/logos/tagit-logo.jpg",  live:false },
];

function ConstellationCanvas({ dark }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const ctx = canvas.getContext("2d");
    const starColor = dark ? "255,255,255" : "80,100,200";
    const lineColor = dark ? "180,200,255" : "60,80,200";
    const stars = Array.from({length: 120}, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.18, vy: (Math.random() - 0.5) * 0.18,
      r: Math.random() * 1.8 + 0.4,
      twinkle: Math.random() * Math.PI * 2, twinkleSpeed: Math.random() * 0.03 + 0.01,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.x += s.vx; s.y += s.vy; s.twinkle += s.twinkleSpeed;
        if (s.x < 0 || s.x > canvas.width)  s.vx *= -1;
        if (s.y < 0 || s.y > canvas.height) s.vy *= -1;
        const alpha = 0.5 + 0.5 * Math.sin(s.twinkle);
        const grd = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 3);
        grd.addColorStop(0, `rgba(${starColor},${alpha})`);
        grd.addColorStop(1, `rgba(${starColor},0)`);
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r * 3, 0, Math.PI * 2);
        ctx.fillStyle = grd; ctx.fill();
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${starColor},${alpha})`; ctx.fill();
      });
      stars.forEach((a, i) => stars.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 120) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(${lineColor},${0.15 * (1 - d / 120)})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [dark]);
  return <canvas ref={canvasRef} style={{ position:"fixed", inset:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:0 }} />;
}

function CompanyCard({ c, sel, setSel, dark, t, onLaunch }) {
  const isSelected = sel === c.id;
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={() => setSel(c.id)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: isSelected ? t.cardSel : t.card,
        backdropFilter:"blur(28px)", WebkitBackdropFilter:"blur(28px)",
        border:`1px solid ${isSelected ? c.color+"55" : t.border}`,
        borderRadius:28, padding:"40px 24px 32px", cursor:"pointer",
        transition:"all 0.4s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: isSelected
          ? dark?`0 24px 60px ${c.color}30`:`0 20px 50px ${c.color}18`
          : dark?"0 4px 24px rgba(0,0,0,0.3)":"0 4px 20px rgba(0,20,120,0.07)",
        display:"flex", flexDirection:"column", alignItems:"center", gap:16,
        position:"relative", overflow:"hidden",
        transform: isSelected ? "translateY(-6px)" : hov ? "translateY(-3px)" : "translateY(0)",
      }}>
      {/* Top accent */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3,
        background:`linear-gradient(90deg,transparent,${c.color}${isSelected?"cc":"44"},transparent)`,
        transition:"all 0.4s" }} />
      {/* Shimmer */}
      <div style={{ position:"absolute", inset:0, pointerEvents:"none",
        background:`linear-gradient(110deg,transparent 30%,${t.shimmer} 50%,transparent 70%)` }} />
      {/* Glow */}
      <div style={{ position:"absolute", top:-40, left:"50%", transform:"translateX(-50%)",
        width:180, height:100, background:c.color, borderRadius:"50%",
        opacity: isSelected?0.15:hov?0.08:0.04, filter:"blur(30px)",
        transition:"opacity 0.4s", pointerEvents:"none" }} />

      {/* LIVE / COMING SOON badge */}
      <div style={{
        position:"absolute", top:14, right:14,
        background: c.live ? "rgba(34,197,94,0.15)" : "rgba(249,115,22,0.12)",
        border: `1px solid ${c.live ? "rgba(34,197,94,0.4)" : "rgba(249,115,22,0.35)"}`,
        borderRadius:20, padding:"3px 10px",
        fontSize:9, fontWeight:800, letterSpacing:1.2,
        color: c.live ? "#22c55e" : "#f97316",
      }}>
        {c.live ? "● LIVE" : "⚙ IN DEVELOPMENT"}
      </div>

      {/* Logo */}
      <div style={{ width:150, height:150, borderRadius:30, overflow:"hidden",
        background:"white", display:"flex", alignItems:"center", justifyContent:"center",
        border:`3px solid ${isSelected ? c.color+"aa" : c.color+"55"}`,
        boxShadow: isSelected
          ? `0 0 0 6px ${c.color}22, 0 12px 40px ${c.color}55`
          : `0 6px 24px ${c.color}44`,
        transition:"all 0.4s",
        animation: isSelected ? "floatY 3s ease-in-out infinite" : "none",
        filter: !c.live && !isSelected ? "grayscale(20%)" : "none" }}>
        <img src={c.logo} alt={c.name}
          style={{ width:"92%", height:"92%", objectFit:"contain" }}
          onError={e => {
            e.target.style.display="none";
            e.target.parentNode.style.background=`linear-gradient(135deg,${c.color},${c.g2})`;
            e.target.parentNode.style.color="white";
            e.target.parentNode.style.fontWeight="900";
            e.target.parentNode.style.fontSize="22px";
            e.target.parentNode.innerText=c.name;
          }} />
      </div>

      {/* Name */}
      <div style={{ color:t.title, fontWeight:900, fontSize:22, letterSpacing:0.3 }}>{c.name}</div>

      {/* Bar indicator */}
      <div style={{ display:"flex", gap:5, alignItems:"center" }}>
        {[32,12,7].map((w,i) => (
          <div key={i} style={{ height:3, borderRadius:4,
            width: isSelected ? w : i===0?12:i===1?7:4,
            background: i===0
              ? isSelected ? `linear-gradient(90deg,${c.color},${c.g2})` : dark?"rgba(255,255,255,0.12)":"rgba(0,20,120,0.1)"
              : isSelected ? `${c.color}${i===1?"77":"44"}` : dark?"rgba(255,255,255,0.07)":"rgba(0,20,120,0.06)",
            boxShadow: isSelected&&i===0 ? `0 0 10px ${c.color}` : "none",
            transition:"all 0.4s" }} />
        ))}
      </div>

      {isSelected && (
        <div style={{ background:`${c.color}25`, border:`1px solid ${c.color}88`,
          borderRadius:50, padding:"5px 20px", fontSize:12, fontWeight:800,
          color: c.color, boxShadow:`0 0 12px ${c.color}44`,
          letterSpacing:0.5, animation:"fadeUp 0.3s ease" }}>✓ Selected</div>
      )}
    </div>
  );
}

export default function HomePage() {
  const [dark, setDark] = useState(false);
  const [sel,  setSel]  = useState(null);
  const navigate = useNavigate();

  const t = dark ? {
    bg:"#040d2e", card:"rgba(10,30,100,0.45)", cardSel:"rgba(10,40,120,0.75)",
    border:"rgba(80,140,255,0.2)", title:"#e8f0ff", sub:"#7aaeff", label:"#5588ff",
    ctaBg:"rgba(10,30,100,0.5)", ctaBorder:"rgba(80,140,255,0.2)", shimmer:"rgba(100,160,255,0.06)",
  } : {
    bg:"#c8d3f0", card:"rgba(255,255,255,0.55)", cardSel:"rgba(255,255,255,0.75)",
    border:"rgba(0,20,120,0.22)", title:"#020c2e", sub:"#0a237a", label:"#0a237a",
    ctaBg:"rgba(255,255,255,0.70)", ctaBorder:"rgba(0,20,120,0.22)", shimmer:"rgba(255,255,255,0.3)",
  };

  const handleLaunch = () => {
    if (!sel) return;
    const company = sel === "group" ? null : companies.find(c => c.id === sel);
    if (sel === "group") {
      navigate("/dashboard", { state: { company: "TSI" } });
    } else if (company?.live) {
      navigate("/dashboard", { state: { company: company.name } });
    } else {
      navigate("/coming-soon", { state: { company } });
    }
  };

  const selCompany = companies.find(c => c.id === sel);

  return (
    <div style={{ background:t.bg, minHeight:"100vh", fontFamily:"Inter,sans-serif",
      transition:"background 0.5s", position:"relative", overflow:"hidden" }}>

      <ConstellationCanvas dark={dark} />

      <div style={{ position:"fixed", top:-120, left:-100, width:500, height:500, borderRadius:"50%",
        background: dark?"radial-gradient(circle,rgba(0,80,255,0.25),transparent 70%)":"radial-gradient(circle,rgba(99,102,241,0.1),transparent 70%)",
        pointerEvents:"none" }} />
      <div style={{ position:"fixed", bottom:-100, right:-80, width:450, height:450, borderRadius:"50%",
        background: dark?"radial-gradient(circle,rgba(0,60,200,0.2),transparent 70%)":"radial-gradient(circle,rgba(124,58,237,0.08),transparent 70%)",
        pointerEvents:"none" }} />

      <div style={{ position:"relative", zIndex:1, minHeight:"100vh",
        display:"flex", flexDirection:"column", padding:"0 48px" }}>

        {/* HEADER */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"24px 0 20px",
          borderBottom:`1px solid ${dark?"rgba(255,255,255,0.06)":"rgba(0,20,120,0.08)"}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ width:52, height:52, borderRadius:16, overflow:"hidden",
              background:"white", display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow: dark?"0 0 40px #0066ff55":"0 8px 24px rgba(0,102,255,0.28)",
              border:"2px solid rgba(255,255,255,0.4)", flexShrink:0 }}>
              <img src={GROUP_LOGO} alt="TSI Groups"
                style={{ width:"90%", height:"90%", objectFit:"contain" }}
                onError={e => { e.target.style.display="none"; e.target.parentNode.innerText="TSI"; }} />
            </div>
            <div style={{ color:t.title, fontWeight:900, fontSize:22, letterSpacing:-0.5 }}>TSI Groups</div>
          </div>
          <button onClick={() => setDark(d => !d)} style={{
            background: dark?"rgba(255,255,255,0.08)":"rgba(255,255,255,0.9)",
            border: dark?"1px solid rgba(255,255,255,0.15)":"1px solid rgba(0,20,120,0.12)",
            borderRadius:50, padding:"8px 20px", cursor:"pointer",
            display:"flex", alignItems:"center", gap:8,
            backdropFilter:"blur(12px)", transition:"all 0.4s" }}>
            <span style={{ fontSize:16 }}>{dark ? "☀️" : "🌙"}</span>
            <span style={{ color: dark?"rgba(255,255,255,0.8)":"#1e3a8a", fontSize:13, fontWeight:700 }}>
              {dark ? "Light Mode" : "Dark Mode"}
            </span>
          </button>
        </div>

        {/* MAIN */}
        <div style={{ flex:1, display:"flex", flexDirection:"column",
          justifyContent:"center", paddingTop:32, paddingBottom:40 }}>

          {/* COMBINED ROW */}
          <div onClick={() => setSel("group")} style={{
            background: sel==="group" ? t.cardSel : t.card,
            backdropFilter:"blur(28px)", WebkitBackdropFilter:"blur(28px)",
            border:`1px solid ${sel==="group" ? (dark?"rgba(0,212,255,0.5)":"rgba(0,102,255,0.4)") : t.border}`,
            borderRadius:24, padding:"24px 32px", cursor:"pointer", marginBottom:36,
            transition:"all 0.4s cubic-bezier(0.4,0,0.2,1)",
            boxShadow: sel==="group"
              ? dark?"0 20px 60px rgba(0,212,255,0.18)":"0 16px 48px rgba(0,102,255,0.14)"
              : dark?"0 4px 24px rgba(0,0,0,0.4)":"0 4px 20px rgba(0,20,120,0.07)",
            display:"flex", alignItems:"center", justifyContent:"space-between",
            position:"relative", overflow:"hidden",
            transform: sel==="group" ? "translateY(-3px)" : "translateY(0)" }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:2,
              background: sel==="group"
                ? "linear-gradient(90deg,transparent,rgba(0,212,255,0.9),rgba(191,95,255,0.7),transparent)"
                : "none" }} />
            <div style={{ display:"flex", alignItems:"center", gap:20 }}>
              <div style={{ display:"flex" }}>
                {companies.map((c, i) => (
                  <div key={c.id} style={{ width:48, height:48, borderRadius:14, overflow:"hidden",
                    background:"white", display:"flex", alignItems:"center", justifyContent:"center",
                    marginLeft:i===0?0:-14, zIndex:companies.length-i, position:"relative",
                    border:"2px solid rgba(255,255,255,0.5)", boxShadow:`0 4px 12px ${c.color}44` }}>
                    <img src={c.logo} alt={c.name}
                      style={{ width:"85%", height:"85%", objectFit:"contain" }}
                      onError={e => {
                        e.target.style.display="none";
                        e.target.parentNode.style.background=`linear-gradient(135deg,${c.color},${c.g2})`;
                        e.target.parentNode.style.color="white";
                        e.target.parentNode.style.fontWeight="900";
                        e.target.parentNode.innerText=c.name.slice(0,2);
                      }} />
                  </div>
                ))}
              </div>
              <div>
                <div style={{ color:t.title, fontWeight:800, fontSize:18 }}>TSI Groups Dashboard</div>
                <div style={{ color:t.sub, fontSize:13, marginTop:3, fontWeight:600 }}>
                  TSI · Sysarc · AIS · TagIT 
                </div>
              </div>
            </div>
            <div style={{
              background: sel==="group" ? dark?"rgba(0,212,255,0.14)":"rgba(0,102,255,0.08)" : dark?"rgba(255,255,255,0.06)":"rgba(0,20,120,0.05)",
              border:`1px solid ${sel==="group" ? dark?"rgba(0,212,255,0.4)":"rgba(0,102,255,0.28)" : t.border}`,
              borderRadius:50, padding:"9px 24px", fontSize:14, fontWeight:700,
              color: sel==="group" ? (dark?"#00d4ff":"#0066ff") : t.sub,
              transition:"all 0.3s", whiteSpace:"nowrap" }}>
              {sel==="group" ? "✓ Selected" : "View All →"}
            </div>
          </div>

          {/* COMPANY CARDS */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:20 }}>
            {companies.map(c => (
              <CompanyCard key={c.id} c={c} sel={sel} setSel={setSel} dark={dark} t={t} />
            ))}
          </div>
        </div>

        {/* LAUNCH BAR */}
        <div style={{ padding:"0 0 28px" }}>
          <div style={{ background:t.ctaBg, backdropFilter:"blur(24px)",
            border:`1px solid ${t.ctaBorder}`, borderRadius:20, padding:"18px 32px",
            display:"flex", alignItems:"center", justifyContent:"space-between",
            opacity:sel?1:0.2, transition:"all 0.4s", pointerEvents:sel?"auto":"none" }}>
            <div>
              <p style={{ color:t.label, fontSize:11, margin:0, textTransform:"uppercase", letterSpacing:2, fontWeight:700 }}>
                {sel && selCompany && !selCompany.live ? "⚙ Under Development" : "Ready to launch"}
              </p>
              <p style={{ color:t.title, fontWeight:800, fontSize:18, margin:"4px 0 0" }}>
                {sel==="group" ? "TSI Groups Dashboard"
                  : sel ? selCompany?.name+" Dashboard" : "—"}
              </p>
            </div>
            <button onClick={handleLaunch} style={{
              background: sel && selCompany && !selCompany.live && sel !== "group"
                ? "linear-gradient(135deg,#f97316,#ea580c)"
                : "linear-gradient(135deg,#0066ff,#00d4ff)",
              color:"white", border:"none", borderRadius:50, padding:"14px 36px",
              fontWeight:800, fontSize:15, cursor:"pointer",
              boxShadow: sel && selCompany && !selCompany.live && sel !== "group"
                ? "0 8px 28px rgba(249,115,22,0.4)"
                : "0 8px 28px rgba(0,102,255,0.4)",
              letterSpacing:0.3 }}>
              {sel && selCompany && !selCompany.live && sel !== "group"
                ? "View Status →" : "Launch Dashboard →"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatY { 0%,100%{transform:translateY(-4px)} 50%{transform:translateY(-12px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        * { box-sizing:border-box; }
      `}</style>
    </div>
  );
}